import { useAuthContext } from 'state/auth';
import { selectors, useAppSelector } from 'state/global-state';

/**
 * Whether the content-card slice's session-scoped state can currently be trusted as belonging to
 * the authenticated user (`isCurrent`), plus `sessionId` for tagging request continuations.
 * `sessionOwnerId` only catches up once resetNotificationCenterSession's dispatch commits, so
 * there's a window right after an identity change where it still reflects the previous user -
 * every reader/writer of session-scoped state must gate on `isCurrent` here rather than
 * re-deriving it.
 *
 * `isIdentityResolving` (`useAuthContext().loading`) distinguishes "auth still restoring/refetching"
 * from genuinely logged-out, since both read `sessionIdentity` as `null`.
 */
export const useNotificationCenterSession = () => {
  const { user, loading } = useAuthContext();
  const sessionIdentity = user?.cognitoId ?? null;
  const sessionOwnerId = useAppSelector(selectors.loyalty.selectNotificationCenterSessionOwnerId);
  const sessionId = useAppSelector(selectors.loyalty.selectNotificationCenterSessionId);

  return {
    sessionIdentity,
    isIdentityResolving: loading,
    isCurrent: sessionIdentity !== null && sessionOwnerId === sessionIdentity,
    sessionId,
  };
};
